import React from "react";
import { styled } from "@mui/material";
import type { AppBarProps, ToolbarProps, BoxProps, IconButtonProps, DrawerProps, ButtonProps } from "@mui/material";
import type { StyledComponent } from "@mui/styled-engine";
import { AppBar, Toolbar, Box, Button, IconButton, Drawer } from "@mui/material";

// Constants
const COLORS = {
  active: "#52A8EF",
  white: "#fff",
  glassBackground: "rgba(0, 0, 0, 0.4)",
  glassShadow: "0px 2px 10px rgba(0,0,0,0.3)",
} as const;

const SPACING = {
  transparentPadding: "15px 25px",
  glassPadding: "12px 24px",
  menuItemMargin: "0 20px",
  menuItemPadding: "10px 0 10px 0px",
  menuGap: "16px",
  drawerGap: "20px",
  drawerPadding: "16px 0",
  drawerWidth: 240,
  drawerPaperPadding: "16px",
} as const;

const TYPOGRAPHY = {
  fontFamily: "Open Sans, sans-serif",
  fontSize: 14,
  fontWeight: 400,
} as const;

const TRANSITIONS = {
  underline: "transform 0.3s ease-out",
} as const;

// Transparent AppBar (no background, no shadow)
export const TransparentAppBar: StyledComponent<AppBarProps> = styled(AppBar)(() => ({
  backgroundColor: "transparent",
  boxShadow: "none",
  backgroundImage: "none",
  padding: `${SPACING.transparentPadding} !important` as const,
}));

// Glass AppBar (glassmorphism effect for mobile)
export const GlassAppBar: StyledComponent<AppBarProps> = styled(AppBar)(() => ({
  backgroundColor: COLORS.glassBackground,
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  boxShadow: COLORS.glassShadow,
  padding: SPACING.glassPadding,
}));

// Toolbar wrapper
export const NavToolbar: StyledComponent<ToolbarProps> = styled(Toolbar)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
}));

// Logo box
export const LogoBox: StyledComponent<BoxProps> = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

// Logo image wrapper
export const LogoImage: StyledComponent<BoxProps> = styled(Box)(() => ({
  height: 40,
  display: "flex",
  alignItems: "center",
  "& img": {
    height: "100%",
    width: "auto",
    display: "block",
  },
}));

// User actions container
export const UserActionsBox: StyledComponent<BoxProps> = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  marginLeft: "auto",
}));

// Menu container (desktop)
export const MenuBox: StyledComponent<BoxProps> = styled(Box)(() => ({
  display: "flex",
  gap: SPACING.menuGap,
  alignItems: "center",
}));

// Menu container (mobile drawer)
export const DrawerMenuBox: StyledComponent<BoxProps> = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: SPACING.drawerGap,
  padding: SPACING.drawerPadding,
}));

// Mobile menu button
export const MobileMenuButton: StyledComponent<IconButtonProps> = styled(IconButton)(({ theme }) => ({
  display: "none",
  color: COLORS.white,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  [theme.breakpoints.down("md")]: {
    display: "block",
  },
}));

// Mobile drawer
export const MobileDrawer: StyledComponent<DrawerProps> = styled(Drawer)(() => ({
  "& .MuiDrawer-paper": {
    width: SPACING.drawerWidth,
    padding: SPACING.drawerPaperPadding,
    backgroundColor: '#000',
    color: COLORS.white,
  },
}));

// Menu item (navigation link)
export const MenuItem: StyledComponent<ButtonProps & { active?: boolean }> = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  position: "relative",
  textTransform: "none",
  color: active ? COLORS.active : COLORS.white,
  margin: SPACING.menuItemMargin,
  padding: SPACING.menuItemPadding,
  fontFamily: TYPOGRAPHY.fontFamily,
  fontSize: TYPOGRAPHY.fontSize,
  fontWeight: TYPOGRAPHY.fontWeight,
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "1px",
    backgroundColor: COLORS.active,
    transform: active ? "scaleX(1)" : "scaleX(0)",
    transformOrigin: "bottom right",
    transition: TRANSITIONS.underline,
  },
  "&:hover::after": {
    transform: "scaleX(1)",
    transformOrigin: "bottom right",
  },
  "&:hover": {
    cursor: "pointer",
    color: COLORS.active,
  },
}));